import React, { useState, useEffect } from "react";
import {
  GetLogJobMachineDetailResponseType,
  GetLogJobMachinesResponseType,
} from "../../../models/_publicCall";
import { Toaster } from "react-hot-toast";
import Modal from "react-responsive-modal";
import moment from "moment";
import store from "store2";
import { SiteVisitationSteps, useSiteVisitation, SiteVisitationMachine } from "../../../providers/SiteVisitationProvider";
import machineListImage from "../../../../images/MachineList.png";

const MachineList = (props: {
  logJobMachineDetail:GetLogJobMachineDetailResponseType |null;
  logJobMachineList: GetLogJobMachinesResponseType[];
  setSelectSiteVisitationMachine: (value: {
    [machineId: string]: SiteVisitationMachine;
  }) => void;
  setCurrentStep: (step: number) => void;
  logJobId:any;
}) => {
  const {
    logJobId,
    setSelectSiteVisitationMachine,
    logJobMachineList,
    setCurrentStep,
    logJobMachineDetail,
  } = props;
  const { validateTickets, validateFields, validationErrors, getJobMachines } = useSiteVisitation();
  const [showMachineInfoModal, setShowMachineInfoModal] = useState(false);
  const [invalidMachines, setInvalidMachines] = useState<{ [machineId: string]: boolean }>({});
  const [invalidMachineNames, setInvalidMachineNames] = useState<string[]>([]);
  const [replaceMachineError, setReplaceMachineError] = useState<string | null>(null);

  useEffect(() => {
    const error = store.get("replaceMachineError");
    if (error) {
      setReplaceMachineError(error);
      store.remove("replaceMachineError"); // Clear error after displaying
    }
  }, []);

  const isMachineOnline = (lastHeartbeatAt: string) => {
    const last = moment(lastHeartbeatAt);
    const now = moment();
    return now.diff(last, "minutes") <= 30;
  };

  const handleWorkOnMachine = (machine: any) => {
    const storedMachines = store.get("machines") || {};
    const machineData = storedMachines[machine.id] || {};

    const updatedMachine = {
      ...machine,
      ...machineData,
    };

    setSelectSiteVisitationMachine(updatedMachine);

    setCurrentStep(SiteVisitationSteps.TICKET_INFO);
  };

  const validateMachines = (): string[] => {
    const invalidNames: string[] = [];
    const invalidMachinesState: { [machineId: string]: boolean } = {};
    const storedMachines = getJobMachines(logJobId) || {};
    // Validate machine fields
    logJobMachineList.forEach((machine: any) => {
      const machineData = storedMachines[machine.id] || {};
      const requiredFields = [
        { name: "sanitizerLevel", label: "Sanitizer Level" },
        { name: "blenderId", label: "Blender" },
        { name: "tubeShieldId", label: "Tube Door Shield" },
      ];

      const missingFields = requiredFields
        .filter((field) => !machineData[field.name])
        .map((field) => field.label);

      if (missingFields.length > 0) {
        const fieldLabel = missingFields.length > 1 ? " are" : " is";
        invalidNames.push(`Machine "${machine.name}": ${missingFields.join(", ")} ${fieldLabel} required`);
        invalidMachinesState[machine.id] = true; // Mark machine as invalid
      } else {
        invalidMachinesState[machine.id] = false; // Mark machine as valid
      }
    });

    // Validate tickets
    const ticketErrors = validateTickets(
      logJobMachineDetail?.tickets || [],
      store.get("selectedDiagnosis") || {},
      store.get("selectedOtherDaignosis") || {},
      store.get("selectedMachineServices") || {},
      store.get("selectedOtherMachineServices") || {},
      store.get("replacedBlender") || {},
      store.get("replacedBoard") || {}
    );

    Object.entries(ticketErrors).forEach(([ticketId, error]) => {
      invalidNames.push(`Ticket ID ${ticketId}: ${error}`);
    });
    setInvalidMachines(invalidMachinesState); // Update state with invalid machines
    return invalidNames;
  };
  const jobMachines = getJobMachines(logJobId);
  return (
    <div className="wo-form-section mt-8">
      <header className="header">
        <h2>List of Machines</h2>
      </header>
      <div className="icon-container m-4" onClick={() => setShowMachineInfoModal(true)}>
        <i className="fa fa-question-circle" aria-hidden="true"></i>
      </div>

      <div className="machine-wrapper">
        {logJobMachineList && logJobMachineList.length > 0 ? (
          <>
            {logJobMachineList.map((machine: any, index: number) => {
              const isInvalid = invalidMachines[machine.id];
              const isFinished = machine?.finishWorkOnMachine || jobMachines?.[machine.id]?.finishWorkOnMachine; 
              return (
                <div key={index} className={`machine-card mt-8 mb-7 ${isInvalid ? "border-red" : ""}`}>
                 {isFinished === true &&  <span className="fa fa-check"> </span>} 
                  <div
                    onClick={() => handleWorkOnMachine(machine)}
                  >
                    <p><strong>Machine Name:</strong> {machine.name}</p>
                    <p><strong>Serial Number:</strong> {machine.serialNumber}</p>
                    <p><strong>Last Sanitized:</strong> {machine.lastSanitizerReplacedAt}</p>
                    <p>
                      <strong>Internet Status:</strong>{" "}
                      <span
                        className={`status-text ${
                          isMachineOnline(machine.lastHeartbeatAt) ? "online" : "offline"
                        }`}
                      >
                        {isMachineOnline(machine.lastHeartbeatAt) ? "Online" : "Offline"}
                      </span>
                      <span
                        className={`status-indicator ${
                          isMachineOnline(machine.lastHeartbeatAt) ? "green" : "red"
                        }`}
                      />
                    </p>
                  </div>
                  <p className="work-on-machine">
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => handleWorkOnMachine(machine)}
                    >
                      Work On Machine
                      <i className="fa fa-arrow-right ms-2" aria-hidden="true"></i>
                    </button>
                  </p>
                </div>
              );
            })}
          </>
        ) : (
          <p className="text-muted">No machine data available.</p>
        )}

        {/* ERROR MESSAGE ABOVE BUTTON */}
        {invalidMachineNames.length > 0 && (
          <div className="alert alert-danger mt-4 mb-3 p-3 border border-danger rounded">
            <strong>Please complete required fields for the following:</strong>
            <ul className="mt-2 mb-0 ps-4">
              {invalidMachineNames.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          </div>
        )}

        {replaceMachineError && (
          <div className="alert alert-danger mt-4 mb-3 p-3 border border-danger rounded">
            <strong>{replaceMachineError}</strong>
          </div>
        )}

        <button
          className="btn btn-primary float-end mt-3"
          type="button"
          onClick={() => {
            const invalidNames = validateMachines();
            if (invalidNames.length === 0) {
              setInvalidMachineNames([]);
              setCurrentStep(SiteVisitationSteps.LOCATION_INFO_1);
            } else {
              setInvalidMachineNames(invalidNames);
            }
          }}
        >
          Finished working on machines
        </button>
      </div>

      <Modal
        open={showMachineInfoModal}
        onClose={() => setShowMachineInfoModal(false)}
        center
        classNames={{ root: "content-wrapper" }}
      >
        <div className="modal-body">
          <div className="p-4">
            <div className="m-4">
            <img src={machineListImage} alt="machine" height={500}/>
            </div>
          </div>
        </div>
      </Modal>

      <Toaster
        position="bottom-left"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
          duration: 3000,
        }}
      />
    </div>
  );
};

export default MachineList;
